<template>
    <section ref="section" class="slice slice-hero slice-hero--with-video" aria-label="hero" data-testid="slice-hero-with-video">
        <div class="slice-content">
            <div class="slice-hero--with-video__frame">
                <div class="slice-hero--with-video__media">
                    <div class="slice-hero--with-video__depth">
                        <video
                            v-if="videoSrc"
                            ref="heroVideo"
                            class="slice-hero--with-video__player"
                            :class="{'slice-hero--with-video__player--fade-out': isVideoFadingOut}"
                            autoplay
                            muted
                            loop
                            playsinline
                            preload="auto"
                            :aria-label="videoLabel"
                            data-testid="slice-hero-with-video-player"
                        >
                            <source :src="videoSrc" :type="videoType" />
                        </video>
                    </div>
                    <div class="slice-hero--with-video__scrim" />
                </div>

                <div class="slice-hero--with-video__content">
                    <div class="slice-hero--with-video__copy">
                        <PrismicRichText
                            v-if="isFilled.richText(slice.primary.title)"
                            :field="slice.primary.title"
                            class="slice-hero--with-video__title slice-hero--with-video__reveal slice-hero--with-video__reveal--delay-1"
                            data-testid="slice-hero-with-video-title"
                        />
                        <PrismicRichText
                            v-if="isFilled.richText(slice.primary.description)"
                            :field="slice.primary.description"
                            class="slice-hero--with-video__subtitle slice-hero--with-video__reveal slice-hero--with-video__reveal--delay-1"
                            data-testid="slice-hero-with-video-description"
                        />
                    </div>

                    <VJoyButton
                        v-if="isFilled.link(slice.primary.button) && slice.primary.button.url"
                        :label="slice.primary.button.text"
                        :href="$withLocalePrefix(slice.primary.button.url)"
                        data-component-source="hero-with-video"
                        variant="primary"
                        class="slice-hero--with-video__reveal slice-hero--with-video__reveal--delay-3"
                    />
                    <SearchPanel
                        v-else
                        class="slice-hero--with-video__search slice-hero--with-video__reveal slice-hero--with-video__reveal--delay-3"
                        :search-tab="slice.primary.keywordSearchTab"
                        :search-field-category="slice.primary.categoryLabel"
                        :search-field-location="slice.primary.locationLabel"
                        :search-location-remote-country-label="slice.primary.remoteInCountryLabel"
                        :ai-search-tab="slice.primary.aiSearchTab"
                        :ai-entity-detection-enabled="$ff('one-entry-point-improvements-unlogged-homepage')"
                        brief-origin="home_unlogged"
                        page-source="home_unlogged_try_ai_search"
                        origin="USER_PROMPT_HOMEPAGE_UNLOGGED"
                    />
                </div>
                <HeroUiSequence
                    v-if="showUiSequence"
                    :is-playing="isUiSequencePlaying"
                    :is-paused="isUiSequencePaused"
                    :is-fading-out="isVideoFadingOut"
                    :prompt="sequencePrompt"
                    :cards="sequenceCards"
                />
            </div>
        </div>

        <HeroReassurance v-if="reassuranceItems.length > 2" :items="reassuranceItems" />
    </section>
</template>

<script setup lang="ts">
    import {computed, useTemplateRef} from 'vue';
    import {isFilled} from '@prismicio/helpers';
    import {getSliceComponentProps, PrismicRichText} from '@prismicio/vue';
    import type {HeroSlice} from '@prismicio-types';
    import SearchPanel from '@search/components/SearchPanel.vue';
    import {useComponentTracking} from '#imports';
    import {VJoyButton} from '@maltjoy/core-vue';
    import HeroReassurance from '../components/HeroReassurance.vue';
    import HeroUiSequence from '../components/HeroUiSequence.vue';
    import {HERO_VIDEO_FADE_OUT_DURATION_IN_SECONDS, useHeroSequence} from '../composables/useHeroSequence';

    type HeroWithVideoInZone = Extract<HeroSlice, {variation: 'heroWithVideo'}>;
    const props = defineProps(getSliceComponentProps<HeroWithVideoInZone>());

    const section = useTemplateRef('section');
    useComponentTracking(section);

    const sequenceCardsSource = computed(() => props.slice.primary.cards ?? []);
    const reassuranceItems = computed(() => props.slice.primary.reassurance ?? []);

    const showUiSequence = computed(() => sequenceCardsSource.value.length > 0 || (props.slice.primary.prompt?.length ?? 0) > 0);

    const sequencePrompt = computed(() => {
        const prompt = props.slice.primary.prompt?.[0];
        const firstLine = prompt && isFilled.keyText(prompt.firstLine) ? prompt.firstLine : '';
        const secondLine = prompt && isFilled.keyText(prompt.secondLine) ? prompt.secondLine : '';

        if (!firstLine && !secondLine) {
            return null;
        }

        return {firstLine, secondLine};
    });

    const PROFILE_APPEARANCE = [
        {photoPosition: '50% 22%', score: '5'},
        {photoPosition: '50% 20%', score: '5'},
        {photoPosition: '50% 18%', score: '5'},
    ];

    const sequenceCards = computed(() =>
        sequenceCardsSource.value.slice(0, PROFILE_APPEARANCE.length).map((card, index) => ({
            jobTitle: card.jobTitle ?? '',
            company: card.company ?? '',
            name: card.name ?? '',
            photoPosition: PROFILE_APPEARANCE[index]?.photoPosition ?? '50% 20%',
            photoUrl: isFilled.image(card.image) && card.image.url ? card.image.url : '',
            photoAlt: isFilled.image(card.image) && card.image.alt ? card.image.alt : '',
            review: isFilled.keyText(card.review) ? card.review : '',
            score: PROFILE_APPEARANCE[index]?.score ?? '5',
        })),
    );

    const {fadeOutDuration, isUiSequencePaused, isUiSequencePlaying, isVideoFadingOut, videoLabel, videoSrc, videoType} = useVideo();

    function useVideo() {
        const VIDEO_FILE_PATTERN = /\.(mp4|webm)(\?|#|$)/i;

        const heroVideo = useTemplateRef<HTMLVideoElement>('heroVideo');
        const {isVideoFadingOut, isUiSequencePlaying, isUiSequencePaused} = useHeroSequence(heroVideo);
        const fadeOutDuration = `${HERO_VIDEO_FADE_OUT_DURATION_IN_SECONDS}s`;

        const videoSrc = computed(() => {
            const url = props.slice.primary.videoUrl ?? '';
            // The loop fade follows the media element clock, so only a direct mp4/webm URL can be the background.
            return VIDEO_FILE_PATTERN.test(url) ? url : '';
        });

        const videoType = computed(() => (videoSrc.value.toLowerCase().includes('.webm') ? 'video/webm' : 'video/mp4'));
        const videoLabel = computed(() => props.slice.primary.videoLabel ?? undefined);

        return {fadeOutDuration, isUiSequencePaused, isUiSequencePlaying, isVideoFadingOut, videoLabel, videoSrc, videoType};
    }
</script>

<style lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/text' as text;

    $ease-out: cubic-bezier(0.22, 0.61, 0.36, 1);
    $frame-background: #2a2320;

    .slice-hero {
        &--with-video {
            padding: 0;

            @include mq.screen_md(min) {
                padding: 0 var(--joy-core-spacing-4) var(--joy-core-spacing-4);
            }

            .slice-content {
                max-width: 1900px;
                padding: 0;
                text-align: left;
            }

            &__frame {
                position: relative;
                display: flex;
                min-height: 640px;
                max-height: 1000px;
                align-items: center;
                overflow: hidden;
                background-color: $frame-background;

                @include mq.screen_md(min) {
                    border-radius: 24px;
                }

                @include mq.screen_lg(min) {
                    height: calc(100dvh - var(--topnav-height, 50px) - 24px);
                    min-height: 0;
                }
            }

            &__media {
                position: absolute;
                inset: 0;
                overflow: hidden;
                background-color: $frame-background;
            }

            &__depth {
                position: absolute;
                inset: 0;
            }

            &__player {
                display: block;
                width: 100%;
                height: 100%;
                object-fit: cover;
                object-position: 50% 42%;
                transform: scale(1.12);
                transform-origin: top center;
                transition: opacity v-bind(fadeOutDuration) $ease-out;

                // Fades to the frame background just before the loop restarts, then fades back in.
                &--fade-out {
                    opacity: 0;
                }
            }

            // Top band keeps the transparent navbar readable, left gradient the copy, the diagonal one detaches the
            // white cards on the right, and the flat tint at the bottom of the stack veils the whole video.
            &__scrim {
                position: absolute;
                inset: 0;
                background:
                    linear-gradient(to bottom, rgb(20 8 8 / 52%) 0%, rgb(20 8 8 / 24%) 46%, rgb(20 8 8 / 0%) 100%) top / 100% 200px no-repeat,
                    linear-gradient(to right, rgb(20 8 8 / 42%) 0%, rgb(20 8 8 / 22%) 32%, rgb(20 8 8 / 0%) 58%),
                    linear-gradient(100deg, rgb(20 8 8 / 0%) 30%, rgb(20 8 8 / 10%) 52%, rgb(20 8 8 / 40%) 76%, rgb(20 8 8 / 62%) 100%),
                    linear-gradient(rgb(20 8 8 / 16%), rgb(20 8 8 / 16%));
            }

            &__content {
                position: relative;
                z-index: 3;
                width: 100%;
                max-width: 1440px;
                margin: var(--joy-core-spacing-12) auto 0;
                padding: 0 var(--joy-core-spacing-6);

                .joy-button {
                    margin-top: var(--joy-core-spacing-5);
                }

                @include mq.screen_md(min) {
                    padding: 0 64px;
                }
            }

            &__copy {
                max-width: 620px;
            }

            &__title h1 {
                @include text.getFontBySize(secondary-400);

                max-width: 620px;
                margin: 0;
                color: var(--joy-color-neutral-0);
                text-wrap: balance;

                @include mq.screen_sm(min) {
                    @include text.getFontBySize(secondary-800);
                }
            }

            &__subtitle {
                @include text.getFontBySize(primary-600);

                max-width: 700px;
                margin-top: var(--joy-core-spacing-5);

                p {
                    margin: 0;
                    color: var(--joy-color-neutral-0);
                }
            }

            &__search {
                max-width: 720px;
                margin-top: 34px;
            }

            // An animation and not a transition on a hydration-toggled class: the copy then enters on the first
            // paint of the server-rendered markup, without waiting for the page to become interactive, and the
            // delays below stay comparable to the timestamps of the video, which starts on that same paint.
            &__reveal {
                animation: slice-hero-with-video-reveal 0.4s $ease-out both;

                &--delay-1 {
                    animation-delay: 0.1s;
                }

                &--delay-2 {
                    animation-delay: 0.2s;
                }

                &--delay-3 {
                    animation-delay: 0.3s;
                }
            }
        }
    }

    @keyframes slice-hero-with-video-reveal {
        from {
            opacity: 0;
            transform: translateY(26px);
        }

        to {
            opacity: 1;
            transform: none;
        }
    }

    @media (prefers-reduced-motion: reduce) {
        .slice-hero--with-video__player {
            opacity: 1 !important;
            transition: none !important;
        }

        .slice-hero--with-video__reveal {
            animation: none !important;
            opacity: 1 !important;
            transform: none !important;
        }
    }
</style>
